import { HIDDEN_SYSTEM_IDS } from "./galaxyVisibility";

// Narrative content (16.82, Don's direction). Two bodies of text, both
// pure data with no mechanical effect:
//
// 1. A discovery paragraph per hiddenUntilScanned system (16.46), shown
//    the first time a captain actually reaches it and kept permanently in
//    the Trophy Room afterwards.
// 2. A dossier per NPC archetype, revealed in the Trophy Room only once
//    that opponent has actually been met in space.
//
// **Why this is written and the rest of the storyline still isn't.**
// 16.49 defers storyline/faction diplomacy deliberately -- a plot is
// content, consumed once, and it can't be derived backward from intent
// the way every loop in this game is. These 10 paragraphs are the
// exception that proves the rule: they attach to systems that already
// carry real mechanical weight (Exceptional nodes, an Ancient guardian,
// a discovery bonus -- 16.68), so they explain something the captain has
// already earned rather than gating anything behind reading them. Found
// in any order, they tell one story by accumulation: something older than
// all three charters is still sitting on the richest ore in the galaxy,
// and every faction that found it quietly stopped filing survey reports.

export interface SystemLore {
  systemId: string;
  name: string;
  // A short dossier heading -- what the captain's own logs would call this
  // place, distinct from the system's plain name.
  title: string;
  body: string;
}

export const HIDDEN_SYSTEM_LORE: SystemLore[] = [
  {
    systemId: "S032",
    name: "Redjunction",
    title: "The Junction That Isn't On The Charts",
    body:
      "Two planets, and the Concord's own survey grid runs right past both of them without a single entry. " +
      "The inner world is Earthlike enough to walk on. The outer one is not: its crust carries structures in " +
      "regular arrays, too even for geology and too large for anything that ever launched from here. " +
      "Every ore signature in the system reads a full grade above what a Medium-danger belt has any business " +
      "holding, and the drift patterns say it has been mined before -- carefully, and a very long time ago. " +
      "The Crimson Concord charter claims this space. The Concord's public star catalogue does not list it. " +
      "One of those two facts is a decision somebody made."
  },
  {
    systemId: "S034",
    name: "Thornehome",
    title: "The Drowned Yard",
    body:
      "A single waterworld, and beneath the weather there is metal: Duranium and Neutronium in seams so clean " +
      "they look poured rather than formed. What is left of the orbital track suggests a working yard, not a " +
      "colony -- something was being built here, in volume, and then it stopped all at once. " +
      "Your scanner keeps resolving the same contact on the far side of the planet, holding a station-keeping " +
      "orbit it has evidently held for a very long time. It does not answer hails. It does not need to; " +
      "the wreckage already scattered along that orbit is answer enough about what happens to ships that " +
      "come to take the seams away."
  },
  {
    systemId: "S046",
    name: "Emberbelt",
    title: "The System That Keeps Burning",
    body:
      "The Azure Compact's frontier ends one gate short of here, and the flare cycle explains why -- the star " +
      "throws hard radiation on a schedule nobody wants to plan a shift around. What it does not explain is the " +
      "second planet, whose surface chemistry has no business existing next to a star this violent, or the fact " +
      "that the Volatile Crystal fields sit exactly where the flares can't reach them. " +
      "Shielded. Deliberately. " +
      "Compact survey doctrine says a system this hostile gets a beacon and a wide berth. Somebody came here, " +
      "stayed long enough to work out where the safe shadows were, and then made sure the system stayed off the " +
      "chart instead of filing what they learned."
  },
  {
    systemId: "S048",
    name: "Redhome",
    title: "A Habitable World Nobody Claimed",
    body:
      "Breathable air, stable orbit, four separate ore grades in easy reach, and not one colony charter filed " +
      "against it in the entire history of the Azure Compact. Worlds like this start wars. This one has been " +
      "sitting here, empty, well inside charted space, close enough to a Compact hub that a freighter could " +
      "make the run in a day. " +
      "The surface tells you why nobody stayed: foundations, roadbeds, the outlines of something laid out to a " +
      "plan -- all of it older than the Compact, all of it abandoned rather than destroyed. " +
      "Whoever built here left in good order, and whatever they left behind in orbit is still doing the job " +
      "it was given."
  },
  {
    systemId: "S066",
    name: "Gullethollow",
    title: "The Hollow The Accord Doesn't Discuss",
    body:
      "Solthane Accord space, two gates from a working trade lane, and the system reads as ordinary until you " +
      "put a scanner on the belt. The Titanium and Silicate here are laid out in bands too regular to be " +
      "natural -- not a belt so much as a stockpile, sorted by grade and left in place. " +
      "Accord traders who work this arm of the map know the name and change the subject. The official position " +
      "is that Gullethollow is an empty designation, a numbering error from the first survey. " +
      "The stockpile disagrees. So does the thing keeping watch over it, which acquired your ship the moment " +
      "you cleared the gate and has been holding that lock ever since."
  },
  {
    systemId: "S073",
    name: "Galefall",
    title: "The Cold Store",
    body:
      "One ice moon, one very old idea: put the cache somewhere nothing grows, nothing weathers, and nothing " +
      "has any reason to land. The Duranium and Volatile Crystal here are packed into the ice in deliberate " +
      "lots, and the ice has kept them exactly as they were left. " +
      "This is the deepest the Solthane Accord's frontier reaches, and the last Accord patrol logged through " +
      "here filed a route that goes around the system rather than through it -- a detour of two full days, " +
      "repeated on every subsequent patrol, with no reason recorded. " +
      "Patrols don't add two days to a run for nothing. They add it because somebody came back from here once " +
      "and said don't."
  },
  {
    systemId: "S086",
    name: "Bluehold",
    title: "The Prize The Underforge Never Took",
    body:
      "Underforge crews raid, strip, and move on; that is the whole of their doctrine. Bluehold has Neutronium " +
      "and Xenite Alloy sitting in the open -- the two richest things in the galaxy -- and the Underforge has " +
      "never once tried to hold it. " +
      "Pirates are not sentimental about danger. If a pocket this rich has stayed unworked while crews die " +
      "over ordinary Titanium two gates away, the arithmetic is simple: whatever guards this system has " +
      "already beaten better-armed people than you, often enough that the most opportunistic crews in known " +
      "space have written the loss off and stopped coming. " +
      "The habitable world below is untouched. That should worry you more than the belt does."
  },
  {
    systemId: "S092",
    name: "Ferspire",
    title: "The Ambercrest Silence",
    body:
      "Four ore grades, all of them high, around a waterworld that pirate traffic in this pocket routes around " +
      "so consistently that the empty lane shows up as a shape on the traffic map. You can see the hole in the " +
      "data before you can see the system. " +
      "Ambercrest crews talk constantly -- about scores, about grudges, about who is dead. They do not talk " +
      "about Ferspire, and the one thing every pirate pocket in the galaxy agrees on is that a silence that " +
      "complete is maintained, not accidental. " +
      "Something here made an impression deep enough to survive several generations of crews who have no " +
      "written records, no institutional memory, and no reason to warn anyone else off a rich system except " +
      "the obvious one."
  },
  {
    systemId: "S097",
    name: "Nighthollow",
    title: "Where The Caldcradle Buries Things",
    body:
      "The Caldcradle pocket uses this system as an ending. Ships that have to disappear are brought here and " +
      "left, and the wreck field in the outer orbit is a century of that practice stacked in one place -- " +
      "pirate hulls, faction patrol hulls, and a layer underneath both that predates every registry the three " +
      "charters have ever kept. " +
      "The Neutronium below is untouched because nobody comes here to mine. They come to leave something and " +
      "get out. " +
      "The oldest wrecks are the interesting ones: they are not scuttled, they are shot -- all of them from " +
      "the same angle, all of them by something that was already here when the first of them arrived, and " +
      "which is still holding that same orbit now."
  },
  {
    systemId: "S106",
    name: "Frostvale",
    title: "The Last Entry",
    body:
      "The deepest hidden system in the galaxy, at the far end of the Crescend pocket, and the only one where " +
      "somebody left a message rather than just leaving. " +
      "A survey beacon still runs on the inner world, transmitting on a charter frequency that went out of use " +
      "before the Concord was founded. It repeats a fourteen-second fragment: a position, an ore manifest, and " +
      "a single line stating that the deposit is not unclaimed and that the claimant is still present. " +
      "The manifest matches what your own scanner reads. The claimant is on your screen right now, at the " +
      "edge of the belt, exactly where the beacon says it would be. " +
      "It has been waiting the entire time the message has been playing."
  }
];

export function loreForSystem(systemId: string): SystemLore | undefined {
  return HIDDEN_SYSTEM_LORE.find((l) => l.systemId === systemId);
}

// Every hidden system must have exactly one entry -- a mystery the Trophy
// Room can list but never explain would be worse than not listing it.
// Asserted in loreCodex.test.ts rather than trusted.
export function loreSystemIds(): string[] {
  return HIDDEN_SYSTEM_LORE.map((l) => l.systemId);
}

export const HIDDEN_SYSTEM_COUNT = HIDDEN_SYSTEM_IDS.length;

// ---- NPC dossiers ----------------------------------------------------
// Keyed by the archetype key npcSubtypeKey() already derives from a unit
// name (Raider/Micro/Leader/Line/Logi/Duelist/Elite), so the four tier
// variants of one archetype share a dossier -- the role doesn't change
// between a T1 Raider and a T4 Raider, only the numbers do, and the
// numbers are shown live from the real roster beside it.

export interface NpcDossier {
  // What this unit is for, in one line -- shown under the name.
  role: string;
  body: string;
}

export const NPC_DOSSIERS: Record<string, NpcDossier> = {
  Raider: {
    role: "Pirate line unit — the crew that actually takes your cargo",
    body:
      "The default pirate hull and the one you will meet most: cheap, armed well enough to matter, and flown " +
      "by people who are paid in what they can carry away. It fits no repair of any kind and runs on passive " +
      "shield regeneration alone, which is the whole of its tactical doctrine -- a Raider that is losing does " +
      "not stabilize, it dies or it leaves. " +
      "Pirate economics explain the design. A crew that loses a Raider buys another one; a crew that spends on " +
      "survivability has less to spend on the next hull. Against a captain who can absorb the first exchange, " +
      "a lone Raider is an inconvenience. Against a hauler with a full hold, it is the entire business model."
  },
  Micro: {
    role: "Pirate fodder — the hull tier no legitimate yard builds",
    body:
      "A Micro is what you get when a crew cannot afford a Raider: a single-compartment hull with a weapon " +
      "bolted to it, thrown at targets in numbers because it is worth less than the ammunition spent killing " +
      "it. No other faction fields anything in this class -- the tier exists in the roster because pirates " +
      "invented it, and it stays exclusive to them. " +
      "Treat one as noise. Treat six as a real fight: individually they cannot break a shield, but they share " +
      "the same target-lock behaviour as every other hull in space, and enough of them locking the same plate " +
      "will get through it. Their job is to be in the way while something heavier closes."
  },
  Leader: {
    role: "Pirate command unit — the reason a swarm holds together",
    body:
      "Every mixed pirate group is built around one of these. A Leader carries meaningfully more hull, more " +
      "shield and more output than the Raiders around it, and it is the reason a swarm presses an attack that " +
      "individually none of its members would take on. " +
      "This is the pirate answer to the problem their own economics create -- a faction that fields disposable " +
      "hulls needs something in the formation worth staying for. Kill it and the arithmetic of the fight " +
      "changes immediately: what is left is a collection of hulls that each individually cannot afford to " +
      "trade with you. Leave it alive and you fight the group as a group, for as long as it holds."
  },
  Line: {
    role: "Chartered navy — the hull all three factions actually field",
    body:
      "The Crimson Concord, Azure Compact and Solthane Accord all fly effectively the same line combatant, " +
      "because all three arrived at the same answer to the same problem: hold a lane, survive a contact, and " +
      "still be worth repairing afterwards. It is unremarkable in every individual statistic and difficult to " +
      "kill for exactly that reason. " +
      "You will meet these because you chose to -- a chartered navy does not raid. It responds. Standing with " +
      "the faction flying them decides whether the encounter happens at all, which makes the Line the only " +
      "opponent in the game whose appearance is genuinely your own fault."
  },
  Logi: {
    role: "Chartered support — the ship that decides how long the fight lasts",
    body:
      "The factions are the only group in known space that fields real repair support, and it changes how " +
      "their formations must be fought. A Logi contributes almost nothing offensively and instead sustains the " +
      "hulls around it, which means a faction group with a live Logi in it does not lose ships on the schedule " +
      "your damage output predicts. " +
      "Target priority is not a preference here, it is the fight. Every targeting system in the game -- yours, " +
      "your drones', your escorts' -- defaults to the highest-healing enemy present for this reason. Kill the " +
      "support and the group becomes a straightforward damage race. Ignore it and you will run out of " +
      "Ferrite before they run out of hull."
  },
  Duelist: {
    role: "Alien contact — never in groups, never explained",
    body:
      "Alien hulls do not swarm, do not escort, and have never been observed operating in company with " +
      "anything else. One arrives, one fights, and the engagement resolves one way or the other. No charter " +
      "has a name for the species, a language for it, or a single recovered example of the hull intact. " +
      "What is known is structural: the shape has no hard edges, the plating grows in layers rather than " +
      "bolting in panels, and the thing takes far longer to kill than its size predicts. A Duelist is the " +
      "cheapest lesson the Alien faction offers -- long, expensive, survivable if you commit, and completely " +
      "uninterested in your cargo."
  },
  Elite: {
    role: "Alien apex — the longest fight in ordinary space",
    body:
      "The heavy end of the only faction that never brings friends. An Elite carries a second layer of grown " +
      "plating over the same organic hull profile as a Duelist and resolves fights on a timescale that has no " +
      "equivalent anywhere else in the roster -- long enough that Ferrite reserves, not hull points, are " +
      "usually what decides it. " +
      "Nothing about it behaves like a raid. It does not chase cargo, it does not withdraw when losing, and " +
      "it appears in places that have nothing worth taking. Whatever the Alien faction is doing, an Elite is " +
      "the version of it that does not care whether you are in the way."
  },
  Ancient: {
    role: "Guardian — one per hidden system, and it was here first",
    body:
      "Every one of the ten unlisted systems has exactly one of these, and it is always in the same place: " +
      "holding station over the richest deposit in the system, on an orbit it has evidently held far longer " +
      "than any charter has existed. It is an Alien Elite hull, half again as tough and half again as " +
      "dangerous as any Elite you will meet in open space. " +
      "It does not patrol, pursue beyond its ground, or take anything. It guards. The wreck fields around " +
      "these systems are the accumulated record of everyone who assumed that a thing sitting still was a " +
      "thing that could be gone through, and the ore behind it is untouched for exactly that reason -- " +
      "the guardian has never once failed to do the job it was left to do."
  }
};

export function dossierFor(subtypeKey: string): NpcDossier | undefined {
  return NPC_DOSSIERS[subtypeKey];
}
